import { useState } from 'react';

import { Button } from '@components/button';
import { createForm, useCreateFormStore } from '@components/form';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useUpdatePassword } from './hooks';

type ChangePasswordData = {
  oldPassword: string;
  password1: string;
  password2: string;
};

const Form = createForm<ChangePasswordData>();

export const useChangePasswordModal = () => useModal(ChangePasswordModal);

export const ChangePasswordModal = () => {
  const { t } = useScopedTranslate(locale);

  const formStore = useCreateFormStore();
  const [oldPasswordError, setOldPasswordError] = useState([] as string[]);
  const [passwordsMatch, setPasswordsMatch] = useState('');

  const updatePasswordMutation = useUpdatePassword(setOldPasswordError, {
    onSuccess: () => {
      hideModal();
    },
  });

  const minPasswordLength = 6;

  const handleSave = (data: ChangePasswordData) => {
    const isPasswordsSame = (data.password1 ?? '') === (data.password2 ?? '');
    setOldPasswordError([]);

    if (isPasswordsSame) {
      updatePasswordMutation.mutate(data);
    } else {
      setPasswordsMatch(t('passwordDontMatch'));
    }
  };

  const passwordErrors = {
    oldPassword: oldPasswordError[0],
    password1: passwordsMatch,
    password2: passwordsMatch,
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={t('changePassword')} />
      <Modal.Content>
        <Form store={formStore} updateErrors={passwordErrors} onSubmit={handleSave}>
          <Stack space={6}>
            <Stack space={2}>
              <Text
                color="purple80"
                text={t('passwordTooShort', { minPassword: String(minPasswordLength) })}
              />
            </Stack>
            <Stack space={4}>
              <Form.PasswordField
                id="oldPassword"
                label={t('currentPassword')}
                validators={['required']}
              />
              <Form.PasswordField
                id="password1"
                label={t('newPassword')}
                validators={['password', 'required']}
              />
              <Form.PasswordField
                id="password2"
                label={t('confirmPassword')}
                validators={['password', 'required']}
              />
            </Stack>
            <Stack space={2} orientation="horizontal" justify="flex-end">
              <Button variant="secondary" onClick={hideModal} text={t('cancel')} />
              <Form.Submit
                text={t('save')}
                variant="primary-blue"
                isDisabled={updatePasswordMutation.isLoading}
              />
            </Stack>
          </Stack>
        </Form>
      </Modal.Content>
    </Modal>
  );
};

export const locale = {
  en: {
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    cancel: 'Cancel',
    save: 'Save',
    passwordTooShort: 'Your password needs to contain at least {minPassword} letters',
    passwordDontMatch: 'Passwords do not match',
  },
  is: {
    changePassword: 'Breyta lykilorði',
    currentPassword: 'Núverandi lykilorð',
    newPassword: 'Nýtt lykilorð',
    confirmPassword: 'Staðfesta nýtt lykilorð',
    cancel: 'Hætta við',
    save: 'Vista',
    passwordTooShort: 'Lykilorðið þarf að innihalda að minnsta kosti {minPassword} stafi',
    passwordDontMatch: 'Lykilorð stemma ekki',
  },
};
